import React, { useState } from 'react';
import './TranslationTable.css';

const TranslationTable = ({ translations, onTranslationsChange }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [filter, setFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('ALL'); // ALL, LOCAL, DEEPL

  // Fonction pour appliquer la règle de casse : minuscules si original en minuscules, sinon majuscules
  const applyCaseRule = (original, text) => {
    if (!original || !text) return text;
    const isLowerCase = original.trim() === original.trim().toLowerCase();
    return isLowerCase ? text.toLowerCase() : text.toUpperCase();
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditValue(translations[index].expressionTraduite);
  };

  const handleSave = (index) => {
    const updatedTranslations = [...translations];
    const original = translations[index].expressionTraduite;

    if (editValue !== original) {
      updatedTranslations[index] = {
        ...updatedTranslations[index],
        expressionTraduite: editValue,
        modified: true
      };
      onTranslationsChange(updatedTranslations);
    }

    setEditingIndex(null);
    setEditValue('');
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      handleSave(index);
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Filtrage
  const filteredTranslations = translations.filter(trans => {
    const matchesSearch = filter === '' ||
      trans.expressionOrigine.toLowerCase().includes(filter.toLowerCase()) ||
      trans.expressionTraduite.toLowerCase().includes(filter.toLowerCase());

    const matchesSource = sourceFilter === 'ALL' || trans.source === sourceFilter;

    return matchesSearch && matchesSource;
  });

  return (
    <div className="translation-table-container">
      {/* Filtres */}
      <div className="table-filters">
        <input
          type="text"
          className="filter-input"
          placeholder="Rechercher une expression..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />

        <div className="source-filters">
          <button
            className={`filter-btn ${sourceFilter === 'ALL' ? 'active' : ''}`}
            onClick={() => setSourceFilter('ALL')}
          >
            Toutes ({translations.length})
          </button>
          <button
            className={`filter-btn filter-local ${sourceFilter === 'LOCAL' ? 'active' : ''}`}
            onClick={() => setSourceFilter('LOCAL')}
          >
            Local ({translations.filter(t => t.source === 'LOCAL').length})
          </button>
          <button
            className={`filter-btn filter-deepl ${sourceFilter === 'DEEPL' ? 'active' : ''}`}
            onClick={() => setSourceFilter('DEEPL')}
          >
            DeepL ({translations.filter(t => t.source === 'DEEPL').length})
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <table className="translation-table">
          <thead>
            <tr>
              <th className="col-num">#</th>
              <th className="col-source">Source</th>
              <th className="col-lang">Langue Origine</th>
              <th className="col-expression">Expression Origine</th>
              <th className="col-lang">Langue Cible</th>
              <th className="col-expression">Expression Traduite</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTranslations.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-results">
                  Aucune traduction trouvée
                </td>
              </tr>
            ) : (
              filteredTranslations.map((trans, index) => {
                const originalIndex = translations.indexOf(trans);
                const isEditing = editingIndex === originalIndex;

                return (
                  <tr
                    key={originalIndex}
                    className={`${trans.modified ? 'row-modified' : ''} ${trans.source === 'LOCAL' ? 'row-local' : 'row-deepl'}`}
                  >
                    <td className="col-num">{index + 1}</td>
                    <td className="col-source">
                      <span className={`badge badge-${trans.source.toLowerCase()}`}>
                        {trans.source}
                      </span>
                      {trans.modified && (
                        <span className="badge badge-modified">MODIFIÉ</span>
                      )}
                    </td>
                    <td className="col-lang">
                      <span className="col-lang-badge">{trans.langueOrigine.toUpperCase()}</span>
                    </td>
                    <td className="col-expression">{applyCaseRule(trans.expressionOrigine, trans.expressionOrigine)}</td>
                    <td className="col-lang">
                      <span className="col-lang-badge">{trans.langueCible.toUpperCase()}</span>
                    </td>
                    <td className="col-expression">
                      {isEditing ? (
                        <input
                          type="text"
                          className="edit-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, originalIndex)}
                          onBlur={() => handleSave(originalIndex)}
                          autoFocus
                        />
                      ) : (
                        <span>{applyCaseRule(trans.expressionOrigine, trans.expressionTraduite)}</span>
                      )}
                    </td>
                    <td className="col-actions">
                      {isEditing ? (
                        <div className="action-buttons">
                          <button
                            className="btn-action btn-save"
                            onClick={() => handleSave(originalIndex)}
                            title="Sauvegarder (Enter)"
                          >
                            ✓
                          </button>
                          <button
                            className="btn-action btn-cancel"
                            onClick={handleCancel}
                            title="Annuler (Escape)"
                          >
                            ✗
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn-action btn-edit"
                          onClick={() => handleEdit(originalIndex)}
                          title="Modifier"
                        >
                          ✏️
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Info */}
      <div className="table-info">
        Affichage de {filteredTranslations.length} sur {translations.length} traductions
      </div>
    </div>
  );
};

export default TranslationTable;
